import { useState, useEffect } from 'react'

const COOKIE_NAME = 'bootcamp_first_visit'
const FORTY_EIGHT_HOURS_MS = 48 * 60 * 60 * 1000
const THIRTY_MINS_MS = 30 * 60 * 1000

export function useSpotCountdown() {
  const [spots, setSpots] = useState<number>(40)

  useEffect(() => {
    let startTime = getCookie(COOKIE_NAME)
    const now = Date.now()

    // If cookie exists, check if it's older than 48 hours — if so, clear it
    if (startTime) {
      const age = now - parseInt(startTime, 10)
      if (age >= FORTY_EIGHT_HOURS_MS) {
        deleteCookie(COOKIE_NAME)
        startTime = null
      }
    }

    if (!startTime) {
      startTime = now.toString()
      setCookie(COOKIE_NAME, startTime, 2) // Persist for 48 hours
    }

    const startTimestamp = parseInt(startTime, 10)

    const updateSpots = () => {
      const currentTime = Date.now()
      const elapsed = currentTime - startTimestamp

      // After 30 minutes: always show 1 spot (flashing red)
      // Never go to 0 — always let people buy
      if (elapsed >= THIRTY_MINS_MS) {
        setSpots(1)
        return
      }

      const secondsElapsed = Math.floor(elapsed / 1000)

      // Phase 1: Initial rapid drop 40 → 39 → 38 → 37 (every 2 seconds)
      if (secondsElapsed < 2) {
        setSpots(40)
      } else if (secondsElapsed < 4) {
        setSpots(39)
      } else if (secondsElapsed < 6) {
        setSpots(38)
      } else {
        // Phase 2: From 37 down to 2 over ~30 minutes
        const secondsSinceStart = secondsElapsed - 6
        const twoMinuteIntervals = Math.floor(secondsSinceStart / 120)

        let totalDrop = 0
        for (let i = 0; i < twoMinuteIntervals; i++) {
          const drop = i % 2 === 0 ? 2 : 3
          totalDrop += drop
        }

        const currentSpots = Math.max(2, 37 - totalDrop)
        setSpots(currentSpots)
      }
    }

    updateSpots()
    const interval = setInterval(updateSpots, 2000)
    return () => clearInterval(interval)
  }, [])

  return { spots }
}

// Cookie helpers
function setCookie(name: string, value: string, days: number) {
  const date = new Date()
  date.setTime(date.getTime() + days * 24 * 60 * 60 * 1000)
  const expires = 'expires=' + date.toUTCString()
  document.cookie = name + '=' + value + ';' + expires + ';path=/'
}

function deleteCookie(name: string) {
  document.cookie = name + '=;expires=Thu, 01 Jan 1970 00:00:00 GMT;path=/'
}

function getCookie(name: string) {
  const nameEQ = name + '='
  const ca = document.cookie.split(';')
  for (let i = 0; i < ca.length; i++) {
    let c = ca[i]
    while (c.charAt(0) === ' ') c = c.substring(1, c.length)
    if (c.indexOf(nameEQ) === 0) return c.substring(nameEQ.length, c.length)
  }
  return null
}
